// Schreiben-Assistent Schritt 2: Empfänger – einzeln/mehrere über die Suche oder alle mehrerer/aller Objekte
// (Mieter, Eigentümer, Dienstleister, Kontakte; Aktiv-Regeln wie Serienmail). Höchstens MAX_EMPFAENGER.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Buildings, SpinnerGap, Trash, UserCircle, WarningCircle, X } from '@phosphor-icons/react';
import { ImmoButton, ObjektPicker, type ObjektPickerOption } from '../../../components/immobilien/ui';
import { supabase } from '../../../lib/supabase/client';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import type { Zielgruppe } from '../../../lib/serienmail/typen';
import { ladeObjektEmpfaenger, ladeSchreibenEmpfaenger } from '../../../lib/schreiben/empfaenger';
import { MAX_EMPFAENGER, type SchreibenEmpfaenger } from '../../../lib/schreiben/typen';
import { EmpfaengerSuche } from '../dokumente/vorlagen/BlankoBriefDialog';
import { LABEL } from '../emails/bereich/Formular';
import { schluessel, type SetZustand, type Zustand } from './zustand';

const ART: Record<string, string> = { mieter: 'Mieter', eigentuemer: 'Eigentümer', kontakt: 'Kontakt', dienstleister: 'Dienstleister' };
const GRUPPEN: [Zielgruppe, string][] = [['mieter', 'Mieter'], ['eigentuemer', 'Eigentümer'], ['dienstleister', 'Dienstleister'], ['kontakt', 'Sonstige Kontakte']];

export function SchrittEmpfaenger({ zustand, setZustand }: { zustand: Zustand; setZustand: SetZustand }) {
  const { mobil } = useMobilModus();
  const [objekte, setObjekte] = useState<{ id: string; bezeichnung: string; objektnummer: string | null; strasse: string | null; hausnummer: string | null; plz: string | null; stadt: string | null }[]>([]);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Björn: mehrere Objekte oder alle auf einmal (Grenze MAX_EMPFAENGER bleibt – größere Runden über die Serienmail)
  const [objektIds, setObjektIds] = useState<string[]>(params.get('objekt') ? [params.get('objekt')!] : []);
  const [zuViele, setZuViele] = useState(false);
  const [gruppen, setGruppen] = useState<Set<Zielgruppe>>(new Set(['mieter']));
  const [laedt, setLaedt] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  // #588: Objekte mit Nummer, Anschrift und Ort – für die Auswahl mit Suche
  useEffect(() => {
    void supabase.from('objekte').select('id, bezeichnung, objektnummer, strasse, hausnummer, plz, stadt').eq('ist_verwaltung', false).order('objektnummer')
      .then(({ data }) => setObjekte((data ?? []) as typeof objekte), () => setObjekte([]));
  }, []);
  const pickerOptionen: ObjektPickerOption[] = useMemo(() => objekte.filter((o) => !objektIds.includes(o.id)).map((o) => {
    const adresse = [o.strasse, o.hausnummer].filter(Boolean).join(' ');
    const ort = [o.plz, o.stadt].filter(Boolean).join(' ');
    return { id: o.id, nummer: o.objektnummer ?? '', label: o.bezeichnung || adresse || 'Objekt', sub: [adresse !== o.bezeichnung ? adresse : '', ort].filter(Boolean).join(', '),
      suchtext: [o.objektnummer, adresse, ort].filter(Boolean).join(' ') };
  }), [objekte, objektIds]);

  const hinzu = (neu: SchreibenEmpfaenger[]) => setZustand((z) => {
    const da = new Set(z.empfaenger.map(schluessel));
    const frei = neu.filter((e) => !da.has(schluessel(e)));
    const platz = MAX_EMPFAENGER - z.empfaenger.length;
    if (frei.length > platz) setMeldung(`Höchstens ${MAX_EMPFAENGER} Empfänger je Schreiben – ${frei.length - platz} nicht übernommen. Größere Rundschreiben bitte über den Serienmail-Assistenten.`);
    return { ...z, empfaenger: [...z.empfaenger, ...frei.slice(0, Math.max(0, platz))] };
  });

  const einzeln = async (t: Parameters<typeof ladeSchreibenEmpfaenger>[0]) => {
    setLaedt(true); setMeldung(null);
    try { hinzu([await ladeSchreibenEmpfaenger(t)]); } catch (e) { setMeldung((e as Error).message); } finally { setLaedt(false); }
  };

  const ausObjekt = async () => {
    if (!objektIds.length || gruppen.size === 0) return;
    setLaedt(true); setMeldung(null); setZuViele(false);
    try {
      const platz = MAX_EMPFAENGER - zustand.empfaenger.length;
      const r = await ladeObjektEmpfaenger(objektIds, [...gruppen], new Set(zustand.empfaenger.map(schluessel)), platz);
      if (!r.passt) {
        setZuViele(true);
        setMeldung(`Das wären ${r.gesamt} Empfänger – ein Schreiben geht an höchstens ${MAX_EMPFAENGER}${platz < MAX_EMPFAENGER ? ` (noch ${platz} frei)` : ''}. Bitte weniger Objekte wählen oder für ein großes Rundschreiben die Serienmail nutzen.`);
        return;
      }
      hinzu(r.empfaenger);
      if (r.gesamt === 0) setMeldung('Keine (weiteren) aktiven Empfänger dieser Gruppen in den gewählten Objekten.');
    } catch (e) { setMeldung((e as Error).message); } finally { setLaedt(false); }
  };

  const entferne = (k: string) => setZustand((z) => ({ ...z, empfaenger: z.empfaenger.filter((e) => schluessel(e) !== k) }));

  const karte: React.CSSProperties = { borderRadius: 22, padding: mobil ? 12 : 16, gap: 10 };
  return (
    <section className="flex flex-col" style={{ gap: 12 }}>
      <div className="grid" style={{ gridTemplateColumns: mobil ? '1fr' : 'repeat(2, minmax(0,1fr))', gap: 12 }}>
        {/* #591: Glas (backdrop-filter) bildet eine eigene Ebene – die Trefferliste läge sonst unter den folgenden Karten */}
        <div className="immo-glas flex flex-col" style={{ ...karte, position: 'relative', zIndex: 3 }}>
          <span style={LABEL}>Einzeln oder mehrere</span>
          <EmpfaengerSuche onWahl={(t) => void einzeln(t)} />
        </div>
        <div className="immo-glas flex flex-col" style={karte}>
          <span style={LABEL}>Alle in einem, mehreren oder allen Objekten</span>
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <ObjektPicker optionen={pickerOptionen} wert={null} leerLabel="Objekt hinzufügen …"
                onWahl={(id) => setObjektIds((l) => (l.includes(id) ? l : [...l, id]))} />
            </div>
            <ImmoButton groesse={44} onClick={() => setObjektIds(objektIds.length === objekte.length ? [] : objekte.map((o) => o.id))}>
              {objektIds.length === objekte.length && objekte.length ? 'Keine Objekte' : 'Alle Objekte'}
            </ImmoButton>
          </div>
          {objektIds.length > 0 && (
            <div className="flex flex-wrap" style={{ gap: 6, maxHeight: 120, overflowY: 'auto' }}>
              {objektIds.length === objekte.length && objekte.length > 6
                ? <span style={{ fontSize: 12.5, fontWeight: 700 }}>Alle {objekte.length} Objekte</span>
                : objektIds.map((id) => (
                  <span key={id} className="flex items-center" style={{ gap: 4, fontSize: 12.5, fontWeight: 700, padding: '4px 4px 4px 10px', borderRadius: 10, background: 'var(--fill2)' }}>
                    {objekte.find((o) => o.id === id)?.bezeichnung ?? 'Objekt'}
                    <button type="button" aria-label="Objekt entfernen" onClick={() => setObjektIds((l) => l.filter((x) => x !== id))}
                      style={{ width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink2)', cursor: 'pointer' }}><X size={11} weight="bold" /></button>
                  </span>
                ))}
            </div>
          )}
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {GRUPPEN.map(([k, l]) => (
              <label key={k} className="flex items-center" style={{ gap: 6, fontSize: 13, minHeight: 36, padding: '0 10px', borderRadius: 10, background: 'var(--fill2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={gruppen.has(k)} onChange={(e) => setGruppen((g) => { const n = new Set(g); if (e.target.checked) n.add(k); else n.delete(k); return n; })} />{l}
              </label>
            ))}
          </div>
          <ImmoButton icon={laedt ? SpinnerGap : Buildings} onClick={() => void ausObjekt()} disabled={!objektIds.length || gruppen.size === 0 || laedt}>Hinzufügen</ImmoButton>
        </div>
      </div>

      {meldung && (
        <span role="status" className="flex flex-wrap items-start" style={{ gap: 6, fontSize: 12.5, color: 'var(--warn)' }}>
          <WarningCircle size={14} weight="fill" style={{ marginTop: 2 }} />{meldung}
          {zuViele && <ImmoButton groesse={32} onClick={() => navigate('/admin/serienmail')}>Zur Serienmail</ImmoButton>}
        </span>
      )}

      <div className="immo-glas flex flex-col" style={karte}>
        <div className="flex items-center justify-between" style={{ gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>{zustand.empfaenger.length} {zustand.empfaenger.length === 1 ? 'Empfänger' : 'Empfänger'}</span>
          {zustand.empfaenger.length > 1 && <ImmoButton groesse={32} icon={Trash} onClick={() => setZustand((z) => ({ ...z, empfaenger: [] }))}>Alle entfernen</ImmoButton>}
        </div>
        {zustand.empfaenger.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch niemand gewählt.</span>}
        <ul className="immo-scroll m-0 flex flex-col overflow-y-auto p-0" style={{ gap: 4, listStyle: 'none', maxHeight: 360 }}>
          {zustand.empfaenger.map((e) => (
            <li key={schluessel(e)} className="flex items-center" style={{ gap: 8, padding: '6px 8px 6px 10px', borderRadius: 12, background: 'var(--fill)' }}>
              <UserCircle size={18} weight="fill" color="var(--acc)" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{e.name} <span style={{ fontWeight: 600, color: 'var(--ink3)' }}>· {ART[e.art]}</span></span>
                {e.sperrvermerk
                  ? <span className="truncate" style={{ fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>Sperrvermerk: {e.sperrvermerk} – wird nicht gesendet</span>
                  : <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{[e.email, e.anschrift.split('\n').slice(-1)[0]].filter(Boolean).join(' · ') || 'keine E-Mail, keine Anschrift'}</span>}
              </span>
              <button type="button" aria-label={`${e.name} entfernen`} onClick={() => entferne(schluessel(e))}
                className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink2)', cursor: 'pointer' }}>
                <X size={13} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
